import React from 'react';
import { UseFormRegister, UseFieldArrayReturn } from 'react-hook-form';
import { Requisition, RequisitionItem } from '../../types';
import Button from './Button';
import Input from './Input';
import Select from './Select';

interface RequisitionItemsTableProps {
  register: UseFormRegister<Requisition>;
  fields: UseFieldArrayReturn<Requisition, 'items'>['fields'];
  append: UseFieldArrayReturn<Requisition, 'items'>['append'];
  remove: UseFieldArrayReturn<Requisition, 'items'>['remove'];
  setValue: (name: any, value: any) => void;
  userRole: 'vessel' | 'office';
  /** Provider names for the supplier dropdown */
  supplierOptions: { value: string; label: string }[];
}

const RequisitionItemsTable: React.FC<RequisitionItemsTableProps> = ({
  register,
  fields,
  append,
  remove,
  setValue,
  userRole,
  supplierOptions,
}) => {
  const addItem = () => {
    const newItem: RequisitionItem = {
      id: Date.now().toString(),
      itemNumber: fields.length + 1,
      item: '',
      qty: 0,
      rob: '',
      appQty: 0,
      unitPrice: 0,
      supplier: '',
      remarks: '',
      status: 'pending',
      selected: false
    };
    append(newItem);
  };

  const removeItem = (index: number) => {
    if (fields.length > 1) {
      remove(index);
      setTimeout(() => {
        fields.forEach((_, i) => {
          if (i > index) {
            setValue(`items.${i}.itemNumber`, i + 1);
          }
        });
      }, 0);
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">Requisition Items</h2>
          <Button type="button" onClick={addItem} variant="secondary" size="sm">
            Add Item
          </Button>
        </div>
      </div>
      <div className="card-body">
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">R.O.B</th>
                {userRole === 'office' && (
                  <>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">App. Qty</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Price</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Supplier</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remarks</th>
                  </>
                )}
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {fields.map((field, index) => (
                <tr key={field.id}>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                    {index + 1}
                  </td>
                  <td className="px-3 py-2">
                    <Input
                      {...register(`items.${index}.item`, { required: 'Item is required' })}
                      placeholder="Item description"
                      className="min-w-[150px]"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Input
                      type="number"
                      step="1"
                      min="0"
                      {...register(`items.${index}.qty`, { 
                        required: 'Quantity is required',
                        min: { value: 0, message: 'Quantity must be 0 or greater' }
                      })}
                      className="min-w-[80px]"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <Input
                      {...register(`items.${index}.rob`)}
                      placeholder="R.O.B"
                      className="min-w-[80px]"
                    />
                  </td>
                  
                  {userRole === 'office' && (
                    <>
                      <td className="px-3 py-2">
                        <Input
                          type="number"
                          step="1"
                          min="0"
                          {...register(`items.${index}.appQty`, { 
                            min: { value: 0, message: 'Approved quantity must be 0 or greater' }
                          })}
                          className="min-w-[80px]"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          {...register(`items.${index}.unitPrice`, { 
                            min: { value: 0, message: 'Unit price must be 0 or greater' }
                          })}
                          className="min-w-[100px]"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Select
                          {...register(`items.${index}.supplier`)}
                          options={supplierOptions}
                          placeholder="Select supplier..."
                          className="min-w-[150px]"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          {...register(`items.${index}.remarks`)}
                          placeholder="Remarks"
                          className="min-w-[120px]"
                        />
                      </td>
                    </>
                  )}
                  <td className="px-3 py-2 whitespace-nowrap">
                    {fields.length > 1 && (
                      <Button
                        type="button"
                        onClick={() => removeItem(index)}
                        variant="danger"
                        size="sm"
                      >
                        Remove
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RequisitionItemsTable;
